import React, { cloneElement} from 'react';
import { Location } from '@reach/router';
import Prism from 'prismjs';
import { Link, StaticQuery, graphql } from 'gatsby';
import classNames from 'classnames';
import { Button } from 'module-tsl-ui-elements';

import CopyToClipboard from '../components/utils/CopyToClipboard';

const USAGE_BASE_URL = 'http://dependency-search.dokku.management.tescloud.com/usages/ui-component/';

const pascalCase = (str) => str.match(/[a-z]+/gi)
  .map((word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase())
  .join('')

const isVersion = (check = true) =>
  ({ type: { name, displayName }}) => check ?
    [name, displayName].indexOf('Version') > -1 :
    [name, displayName].indexOf('Version') === -1;

const getVersionComponentAndNewChildren = (children) => {
  const { props } = children;
  if (Array.isArray(props.children)) {
    const newChildren = props.children.filter(isVersion(false)).map(child => cloneElement(child));
    const versionComponent = props.children.filter(isVersion()).map(child => cloneElement(child))[0];
    return { newChildren, versionComponent };
  }
  return { newChildren: children, versionComponent: null };
}

const Page = ({
  children,
  pageContext: {
    frontmatter: { importSass, importNode, storybook, intro, title },
  },
}) => (
  <StaticQuery
    query={graphql`
      query PrefixQuery {
        site {
          pathPrefix
        }
      }
    `}
    render={({ site: { pathPrefix }}) => (
      <Location>
        {({ location: { pathname } }) => {
          const pathWithoutPrefix = pathname
            .replace(pathPrefix, '')
            .replace(/^\//, '');
          const { newChildren, versionComponent } = getVersionComponentAndNewChildren(children);
          const parent = pathWithoutPrefix.split('/')[0];
          const componentName = title || pascalCase(pathWithoutPrefix.split('/')[1]);
          const codeTo = `/${pathWithoutPrefix.replace(/\/usage(\/)?/, '')}`;
          const usageTo = `/${pathWithoutPrefix.replace(/\/usage(\/)?/, '')}/usage`;
          const isUsageTo = /\/usage(\/)?$/.test(pathname);
          return (
            <React.Fragment>
              <div className="page-header">
                {parent && 
                  <p>
                    {parent}
                  </p>
                }
                <h1>
                  {componentName}
                </h1>
              </div>
              <div className="page-content">
                <div className="page-tabs">
                  <div>
                    <Link className={isUsageTo ? '' : 'is--current'} to={codeTo}>Code</Link>
                    <Link className={!isUsageTo ? '' : 'is--current'} to={usageTo}>Usage</Link>
                  </div>
                  <Button
                    theme="tertiary"
                    target="_blank"
                    href={`${USAGE_BASE_URL}${componentName}`}
                    style={{ marginLeft: 'auto' }}
                  >
                    Check apps dependencies
                  </Button>
                </div>
                {intro && <p className="page-intro-text">{intro}</p>}
                {versionComponent}
                <br />
                {importNode && importSass &&
                  <div className="imports-snippets">
                    <h3 id="imports">
                      {/* eslint-disable-next-line */}
                      <a href="#imports" className="anchor" />
                      imports
                    </h3>
                    <div className="copy-snippet">
                      <i className="icon icon-node" />
                      <CopyToClipboard className="clipboard-code" clipboardText={importNode}>
                        <pre dangerouslySetInnerHTML={{ __html: Prism.highlight(importNode, Prism.languages.javascript, 'javascript') }} />
                      </CopyToClipboard>
                    </div>
                    <div className="copy-snippet">
                      <i className="icon icon-sass" />
                      <CopyToClipboard className="clipboard-code" clipboardText={importSass}>
                        <pre dangerouslySetInnerHTML={{ __html: Prism.highlight(importSass, Prism.languages.css, 'css') }} />
                      </CopyToClipboard>
                    </div>
                  </div>
                }
                <div className={classNames('page-custom-content', { 'page-custom-content--usage': isUsageTo })}>
                  {newChildren}
                </div>
                {storybook &&
                  <React.Fragment>
                    <h3 id="storybook">
                      {/* eslint-disable-next-line */}
                      <a href="#storybook" className="anchor" />
                      storybook
                    </h3>
                    <a href={storybook} rel="noopener noreferrer" target="_blank">View {componentName} storybook stories</a>
                  </React.Fragment>
                }
              </div>
            </React.Fragment>
          )
        }}
      </Location>
    )}
  />
);

export default Page;
